import { Box, SxProps, Theme, useTheme } from "@mui/material";
// Collaboration extensions (optional)
import Collaboration from "@tiptap/extension-collaboration";
import CollaborationCaret from "@tiptap/extension-collaboration-caret";
import Document from "@tiptap/extension-document";
import DragHandle from "@tiptap/extension-drag-handle-react";
import Highlight from "@tiptap/extension-highlight";
import History from "@tiptap/extension-history";
import { ListKit } from "@tiptap/extension-list";
import { Placeholder } from "@tiptap/extension-placeholder";
import { Strike } from "@tiptap/extension-strike";
import { TableKit } from "@tiptap/extension-table";
import TaskItem from "@tiptap/extension-task-item";
import TaskList from "@tiptap/extension-task-list";
import TextAlign from "@tiptap/extension-text-align";
import { Color, TextStyle } from "@tiptap/extension-text-style";
import { TrailingNode } from "@tiptap/extensions";
import { Editor, EditorContent, useEditor } from "@tiptap/react";
// @ts-ignore
import { BubbleMenu } from "@tiptap/react/menus";
import StarterKit from "@tiptap/starter-kit";
import { Button } from "components/button";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { debugConsole } from "utilz/helpers/debugConsole";
import * as awarenessProtocol from "y-protocols/awareness";
import * as Y from "yjs";
import { CodeBlockWithLowlight } from "./extensions/code-block";
import { ForceFirstNodeH1 } from "./extensions/ForcedFirstNodeHeading";
import LinkCard from "./extensions/link-card/linkCardExtension";
import { PdfEmbed } from "./extensions/pdf-embed";
import { ResizableImage } from "./extensions/resizable-image";
import { BubbleMenuList } from "./extensions/slash-command/BubbleMenuList";
import { SlashCommand } from "./extensions/slash-command/slashCommand";
import { TableToolbar, useTableToolbar } from "./extensions/table-toolbar";
import { ImageUploadExtension } from "./extensions/upload";
import FileChipNode from "./extensions/upload/FileNode";
import ImagePlaceholder from "./extensions/upload/ImageNode";
import { VideoEmbed } from "./extensions/video-embed";
import { WordPaste } from "./extensions/word-paste";

const CustomDocument = Document.extend({
  content: "heading block*",
});

interface TipTapEditorProps {
  onSave?: ({
    content,
    htmlContent,
    textContent,
  }: {
    content: Record<string, unknown>;
    htmlContent: string;
    textContent: string;
  }) => void;
  autosave?: boolean;
  autoSaveDuration?: number;
  content?: any;
  onContentChange?: ({
    content,
    htmlContent,
    textContent,
  }: {
    content: Record<string, unknown>;
    htmlContent: string;
    textContent: string;
  }) => void;
  isEditable: boolean;
  forceFirstNodeH1?: boolean;
  // NEW: Optional collaboration props
  enableCollaboration?: boolean;
  yjsDocument?: Y.Doc;
  awarenessProvider?: awarenessProtocol.Awareness;
  collaborationUser?: { name: string; color: string };
  // Display mode without scroll constraints (for shared articles)
  displayMode?: boolean;
  sx?: SxProps<Theme>;
}

const TipTapEditor: React.FC<TipTapEditorProps> = ({
  onSave,
  autosave = false,
  autoSaveDuration = 3000,
  content,
  onContentChange,
  isEditable = true,
  forceFirstNodeH1 = false,
  enableCollaboration = false,
  yjsDocument,
  awarenessProvider,
  collaborationUser,
  displayMode = false,
  sx,
}) => {
  const elementRef = useRef<HTMLDivElement>(null);
  const theme = useTheme();
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastContentRef = useRef<Record<string, unknown>>({});

  const debouncedSave = useCallback(
    ({
      content,
      htmlContent,
      textContent,
    }: {
      content: Record<string, unknown>;
      htmlContent: string;
      textContent: string;
    }) => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }

      saveTimeoutRef.current = setTimeout(() => {
        if (onSave && content !== lastContentRef.current) {
          onSave({
            content,
            htmlContent,
            textContent,
          });
          lastContentRef.current = content;
        }
      }, autoSaveDuration);
    },
    [onSave, autoSaveDuration],
  );

  const editorInstance = useEditor(
    {
      editable: isEditable,
      extensions: [
        ListKit.configure({
          taskList: false,
          taskItem: false,
        }),
        LinkCard,
        FileChipNode,
        ImagePlaceholder,
        ...(forceFirstNodeH1 ? [ForceFirstNodeH1] : []),
        TaskList,
        TaskItem,
        // CustomDocument,
        // TrailingNode.configure({
        //   node: "paragraph",
        // }),
        StarterKit.configure({
          // document: false,
          paragraph: {
            HTMLAttributes: {
              class: "tiptap-paragraph",
            },
          },
          codeBlock: false,
          undoRedo: enableCollaboration ? false : undefined, // Disable undo/redo for collaboration
        }),

        SlashCommand,
        Strike,

        Placeholder.configure({
          placeholder: ({ node, pos }) => {
            // If this is the very first top-level node
            if (pos === 0) {
              return "Untitled";
            }
            if (node.type.name === "heading" && node.attrs.level === 1) {
              return "Heading 1";
            }
            if (node.type.name === "heading" && node.attrs.level === 2) {
              return "Heading 2";
            }
            if (node.type.name === "heading" && node.attrs.level === 3) {
              return "Heading 3";
            }

            return "Start typing or press / for format options";
          },
          showOnlyCurrent: false,
        }),

        TableKit.configure({
          table: { resizable: true },
        }),
        TextAlign.configure({
          types: ["heading", "paragraph"],
          alignments: ["left", "center", "right", "justify"],
          defaultAlignment: "left",
        }),

        ResizableImage,
        Highlight.configure({
          multicolor: true,
        }),
        TextStyle,
        Color,
        ImageUploadExtension,
        VideoEmbed,
        PdfEmbed,
        CodeBlockWithLowlight,
        WordPaste.configure({
          removeStyles: true,
          preserveBold: true,
          preserveItalic: true,
          preserveUnderline: true,
          preserveStrikethrough: true,
          preserveLinks: true,
          preserveImages: true,
          preserveTables: true,
          preserveLists: true,
          preserveHeadings: true,
        }),
        // Conditionally add Collaboration extensions
        ...(enableCollaboration && yjsDocument && awarenessProvider
          ? (() => {
              return [
                Collaboration.configure({
                  document: yjsDocument,
                  field: "default", // TipTap uses "default" as the shared type name
                }),
                CollaborationCaret.configure({
                  provider: awarenessProvider as any,
                  user: collaborationUser || {
                    name: "Anonymous",
                    color: "#FF6B6B",
                  },
                }),
              ];
            })()
          : (() => {
              return [];
            })()),
      ],
      editorProps: {},
      // Only set initial content if NOT in collaboration mode
      // In collaboration mode, content comes from Y.Doc
      content: enableCollaboration
        ? null
        : content
          ? content
          : {
              type: "doc",
              content: [
                { type: "heading", attrs: { level: 1 } },
                { type: "paragraph" },
              ],
            },

      onUpdate: ({ editor }) => {
        const htmlContent = editor.getHTML();
        const textContent = editor.getText();
        const content = editor.getJSON();
        if (autosave) {
          debouncedSave({
            content,
            htmlContent,
            textContent,
          });
        }
        if (onContentChange) {
          onContentChange({
            content,
            htmlContent,
            textContent,
          });
        }
      },
    },
    [enableCollaboration, yjsDocument, awarenessProvider], // Recreate editor when collaboration deps change
  );

  // Alias for backward compatibility within this component
  const editor = editorInstance;

  // Table toolbar hook
  const {
    isVisible: isTableToolbarVisible,
    position: tableToolbarPosition,
    hideToolbar: hideTableToolbar,
  } = useTableToolbar(editor);

  // Update editor content when content prop changes
  useEffect(() => {
    if (editor && content && !enableCollaboration) {
      const currentContent = editor.getJSON();
      // Only update if content is different to avoid unnecessary updates
      if (JSON.stringify(currentContent) !== JSON.stringify(content)) {
        editor.commands.setContent(content);
      }
    }
  }, [editor, content, enableCollaboration]);

  // Update editor editable state when isEditable changes
  useEffect(() => {
    if (editor) {
      editor.setEditable(isEditable);
    }
  }, [editor, isEditable]);

  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);

  return (
    <Box
      sx={{
        width: "100%",
        height: displayMode ? "auto" : "100%",
        p: 2,
        overflow: displayMode ? "visible" : "scroll",
        "& .drag-handle": {
          alignItems: "center",
          cursor: "grab",
          display: isEditable ? "flex" : "none",
          height: "24px",
          justifyContent: "center",
          width: "24px",
          svg: { width: "20px", height: "20px" },
        },

        "& .tiptap": {
          outline: "none",
          minHeight: 200,
          height: "100%",
          // p: "40px",
          ...theme.typography["body-medium"],
          color: theme.palette.lightBg.low,
        },

        // Base paragraph rhythm
        "& .tiptap .tiptap-paragraph": {
          marginTop: 0,
          marginBottom: "16px",
          textIndent: 0,
        },

        "& .tiptap a": {
          cursor: "pointer",
        },
        // No extra gap before the very first block
        "& .tiptap > *:first-child": {
          marginTop: 0,
        },

        // Paragraphs inside list items
        "& .tiptap li .tiptap-paragraph": {
          margin: 0,
          textIndent: 0,
        },

        // No trailing gap at the end
        "& .tiptap > .tiptap-paragraph:last-child": {
          marginBottom: 0,
        },

        // Headings with pixel spacing
        "& .tiptap h1": {
          ...theme.typography["h4"],
          color: theme.palette.lightBg.high,
          marginTop: "24px",
          marginBottom: "16px",
        },
        "& .tiptap h2": {
          ...theme.typography["h5"],
          color: theme.palette.lightBg.high,
          marginTop: "24px",
          marginBottom: "16px",
        },
        "& .tiptap h3": {
          ...theme.typography["h6"],
          color: theme.palette.lightBg.high,
          marginTop: "20px",
          marginBottom: "12px",
        },
        "& .tiptap strong": {
          color: theme.palette.lightBg.high,
        },

        "& .tiptap blockquote": {
          borderLeft: "4px solid #ddd",
          margin: "24px 0",
          paddingLeft: "16px",
          fontStyle: "italic",
          backgroundColor: "#f9f9f9",
        },

        "& .tiptap code": {
          backgroundColor: "#f5f5f5",
          borderRadius: "3px",
          padding: "2px 4px",
          fontFamily: "monospace",
        },

        "& .tiptap pre": {
          backgroundColor: "#1e1e1e",
          color: "#d4d4d4",
          padding: "16px",
          borderRadius: "8px",
          overflow: "auto",
          fontFamily:
            "'Fira Code', 'Monaco', 'Consolas', 'Courier New', monospace",
          fontSize: "14px",
          lineHeight: "1.6",
          margin: "24px 0",
          position: "relative",
        },

        // Syntax highlighting tokens (VS Code dark theme colors)
        "& .tiptap .hljs-keyword": { color: "#569cd6" },
        "& .tiptap .hljs-built_in": { color: "#4ec9b0" },
        "& .tiptap .hljs-type": { color: "#4ec9b0" },
        "& .tiptap .hljs-literal": { color: "#569cd6" },
        "& .tiptap .hljs-number": { color: "#b5cea8" },
        "& .tiptap .hljs-operator": { color: "#d4d4d4" },
        "& .tiptap .hljs-punctuation": { color: "#d4d4d4" },
        "& .tiptap .hljs-property": { color: "#9cdcfe" },
        "& .tiptap .hljs-regexp": { color: "#d16969" },
        "& .tiptap .hljs-string": { color: "#ce9178" },
        "& .tiptap .hljs-char.escape_": { color: "#d7ba7d" },
        "& .tiptap .hljs-subst": { color: "#d4d4d4" },
        "& .tiptap .hljs-symbol": { color: "#dcdcaa" },
        "& .tiptap .hljs-variable": { color: "#9cdcfe" },
        "& .tiptap .hljs-variable.language_": { color: "#4ec9b0" },
        "& .tiptap .hljs-variable.constant_": { color: "#4fc1ff" },
        "& .tiptap .hljs-title": { color: "#dcdcaa" },
        "& .tiptap .hljs-title.class_": { color: "#4ec9b0" },
        "& .tiptap .hljs-title.class_.inherited__": { color: "#4ec9b0" },
        "& .tiptap .hljs-title.function_": { color: "#dcdcaa" },
        "& .tiptap .hljs-attr": { color: "#9cdcfe" },
        "& .tiptap .hljs-attribute": { color: "#9cdcfe" },
        "& .tiptap .hljs-params": { color: "#9cdcfe" },
        "& .tiptap .hljs-comment": { color: "#6a9955", fontStyle: "italic" },
        "& .tiptap .hljs-doctag": { color: "#6a9955" },
        "& .tiptap .hljs-meta": { color: "#9b9b9b" },
        "& .tiptap .hljs-meta .hljs-keyword": { color: "#9b9b9b" },
        "& .tiptap .hljs-meta .hljs-string": { color: "#ce9178" },
        "& .tiptap .hljs-section": { color: "#ffd700" },
        "& .tiptap .hljs-tag": { color: "#808080" },
        "& .tiptap .hljs-name": { color: "#569cd6" },
        "& .tiptap .hljs-bullet": { color: "#d4d4d4" },
        "& .tiptap .hljs-code": { color: "#ce9178" },
        "& .tiptap .hljs-emphasis": { fontStyle: "italic" },
        "& .tiptap .hljs-strong": { fontWeight: "bold" },
        "& .tiptap .hljs-formula": { color: "#4ec9b0" },
        "& .tiptap .hljs-link": {
          textDecoration: "underline",
          color: "#569cd6",
        },
        "& .tiptap .hljs-quote": { color: "#6a9955", fontStyle: "italic" },
        "& .tiptap .hljs-selector-tag": { color: "#d7ba7d" },
        "& .tiptap .hljs-selector-id": { color: "#d7ba7d" },
        "& .tiptap .hljs-selector-class": { color: "#d7ba7d" },
        "& .tiptap .hljs-selector-attr": { color: "#d7ba7d" },
        "& .tiptap .hljs-selector-pseudo": { color: "#d7ba7d" },
        "& .tiptap .hljs-template-tag": { color: "#569cd6" },
        "& .tiptap .hljs-template-variable": { color: "#9cdcfe" },
        "& .tiptap .hljs-addition": {
          backgroundColor: "#144212",
          color: "#e6ffed",
        },
        "& .tiptap .hljs-deletion": {
          backgroundColor: "#600",
          color: "#ffeef0",
        },

        // Code block wrapper styles
        "& .tiptap .code-block-wrapper": {
          position: "relative",
        },

        "& .tiptap .code-block-wrapper pre": {
          paddingTop: "40px", // Extra padding for language selector
        },

        "& .tiptap ul, & .tiptap ol": {
          paddingLeft: "24px",
          margin: "16px 0",
        },

        "& .tiptap hr": {
          border: "none",
          borderTop: "2px solid #ddd",
          margin: "24px 0",
        },

        "& .tiptap > .tiptap-paragraph.is-empty:first-child::before": {
          color: "#adb5bd",
          content: "attr(data-placeholder)",
          float: "left",
          height: "0",
          pointerEvents: "none",
        },

        "& .tiptap table": {
          borderCollapse: "collapse",
          margin: 0,
          overflow: "hidden",
          tableLayout: "fixed",
          width: "100%",
        },

        "& .tiptap table td, & .tiptap table th": {
          border: `1px solid ${theme.palette.divider}`, // was var(--gray-3)
          boxSizing: "border-box",
          minWidth: "1em",
          padding: "6px 8px",
          position: "relative",
          verticalAlign: "top",
        },

        "& .tiptap table td > *, & .tiptap table th > *": {
          marginBottom: 0,
        },

        "& .tiptap table th": {
          backgroundColor: theme.palette.grey[100], // was var(--gray-1)
          fontWeight: 700,
          textAlign: "left",
        },

        "& .tiptap table .selectedCell:after": {
          content: '""',
          position: "absolute",
          zIndex: 2,
          inset: 0,
          backgroundColor: theme.palette.primary.tint,
          pointerEvents: "none",
        },

        "& .tiptap table .column-resize-handle": {
          position: "absolute",
          top: 0,
          right: "-2px",
          bottom: "-2px",
          width: "4px",
          backgroundColor: theme.palette.primary.main, // was var(--purple)
          pointerEvents: "none",
        },

        "& .tiptap .tableWrapper": {
          margin: "24px 0", // 1.5rem
          overflowX: "auto",
        },

        "& .tiptap.resize-cursor": {
          cursor: "col-resize",
        },
        "& .tiptap > *:nth-child(2).is-empty::before": {
          content: "attr(data-placeholder)",
          color: "#adb5bd",
          float: "left",
          height: "0",
          pointerEvents: "none",
        },
        "& .tiptap > *:nth-child(1).is-empty::before": {
          content: "attr(data-placeholder)",
          color: "#adb5bd",
          float: "left",
          height: "0",
          pointerEvents: "none",
        },
        "& .tiptap ul[data-type='taskList']": {
          listStyle: "none",
          marginLeft: 0,
          padding: 0,
        },
        "& .tiptap ul[data-type='taskList'] li": {
          alignItems: "flex-start",
          display: "flex",
        },
        "& .tiptap ul[data-type='taskList'] li > label": {
          flex: "0 0 auto",
          marginRight: "8px",
          userSelect: "none",
        },
        "& .tiptap ul[data-type='taskList'] li > div": {
          flex: "1 1 auto",
        },
        "& .tiptap ul[data-type='taskList'] input[type='checkbox']": {
          cursor: "pointer",
          appearance: "none",
          "-webkit-appearance": "none",
          "-moz-appearance": "none",
          width: "16px",
          height: "16px",
          border: `1px solid ${theme.palette.lightBg.low}`,
          borderRadius: "50%",
          outline: "none",
          verticalAlign: "middle",
          position: "relative",
          "&:checked": {
            backgroundColor: theme.palette.success.main,
            borderColor: theme.palette.success.main,
            "&::after": {
              // prettier-ignore
              content: '""',
              position: "absolute",
              width: "10px",
              height: "10px",
              backgroundColor: theme.palette.success.main,
              borderRadius: "50%",
              transform: "rotate(45deg)",
            },
          },
        },
        "& .tiptap ul[data-type='taskList'] ul[data-type='taskList']": {
          margin: 0,
        },

        // Video embed styles
        "& .tiptap .video-embed-wrapper": {
          position: "relative",
          paddingBottom: "56.25%", // 16:9 aspect ratio
          height: 0,
          overflow: "hidden",
          margin: "24px 0",
          borderRadius: "8px",
          backgroundColor: "#000",
        },

        "& .tiptap .video-embed-wrapper iframe": {
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          border: 0,
          borderRadius: "8px",
        },

        // PDF embed styles
        "& .tiptap .pdf-embed-wrapper": {
          position: "relative",
          margin: "24px 0",
        },

        // Resizable image styles
        "& .tiptap .resizable-image-wrapper": {
          position: "relative",
          margin: "16px 0",
        },
        "& .tiptap .resizable-image-wrapper img": {
          transition: "outline 0.15s ease",
        },

        /* Give a remote user a caret */
        ".collaboration-carets__caret": {
          borderLeft: "1px solid #0d0d0d",
          borderRight: "1px solid #0d0d0d",
          marginLeft: "-1px",
          marginRight: "-1px",
          pointerEvents: "none",
          position: "relative",
          wordBreak: "normal",
        },

        /* Render the username above the caret */
        ".collaboration-carets__label": {
          borderRadius: "4px 4px 4px 0",
          color: theme.palette.lightBg.high,
          padding: "0.1rem 0.3rem",
          position: "absolute",
          top: "-1.4em",
          userSelect: "none",
          whiteSpace: "nowrap",
          left: "-1px",
          ...theme.typography["body-small"],
        },
        ...sx,
      }}
    >
      <Box
        ref={elementRef}
        sx={{ position: "relative", height: displayMode ? "auto" : "90vh" }}
      >
        <DragHandle editor={editor}>
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M9.99528 17.501C9.72017 17.501 9.49508 17.7261 9.49808 18.0012C9.49808 18.2763 9.72317 18.5014 9.99828 18.5014C10.2734 18.5014 10.4985 18.2763 10.4985 18.0012C10.4985 17.7261 10.2734 17.501 9.99528 17.501"
              stroke="#767B85"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M9.99528 11.499C9.72017 11.499 9.49508 11.7241 9.49808 11.9992C9.49808 12.2743 9.72317 12.4994 9.99828 12.4994C10.2734 12.4994 10.4985 12.2743 10.4985 11.9992C10.4985 11.7241 10.2734 11.499 9.99528 11.499"
              stroke="#767B85"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M9.99528 5.49609C9.72017 5.49609 9.49508 5.72119 9.49808 5.9963C9.49808 6.27142 9.72317 6.49651 9.99828 6.49651C10.2734 6.49651 10.4985 6.27142 10.4985 5.9963C10.4985 5.72119 10.2734 5.49609 9.99528 5.49609"
              stroke="#767B85"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M14.9914 17.5068C14.7163 17.5068 14.4912 17.7319 14.4942 18.007C14.4942 18.2822 14.7193 18.5073 14.9944 18.5073C15.2695 18.5073 15.4946 18.2822 15.4946 18.007C15.4946 17.7319 15.2695 17.5068 14.9914 17.5068"
              stroke="#767B85"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M14.9914 11.5049C14.7163 11.5049 14.4912 11.73 14.4942 12.0051C14.4942 12.2802 14.7193 12.5053 14.9944 12.5053C15.2695 12.5053 15.4946 12.2802 15.4946 12.0051C15.4946 11.73 15.2695 11.5049 14.9914 11.5049"
              stroke="#767B85"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M14.9914 5.50195C14.7163 5.50195 14.4912 5.72705 14.4942 6.00216C14.4942 6.27728 14.7193 6.50237 14.9944 6.50237C15.2695 6.50237 15.4946 6.27728 15.4946 6.00216C15.4946 5.72705 15.2695 5.50195 14.9914 5.50195"
              stroke="#767B85"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </DragHandle>

        {editor && (
          <BubbleMenu
            editor={editor}
            options={{ placement: "bottom", offset: 8 }}
            shouldShow={({ editor: currentEditor }: any) => {
              // Don't show in readonly mode
              if (!currentEditor.isEditable) {
                return false;
              }
              // Hide bubble menu when resizableImage is selected
              if (currentEditor.isActive("resizableImage")) {
                return false;
              }
              // Default behavior: show when there's a text selection
              return !currentEditor.state.selection.empty;
            }}
          >
            <BubbleMenuList editor={editor} />
          </BubbleMenu>
        )}
        {/* Table Toolbar */}
        {editor && isEditable && isTableToolbarVisible && (
          <Box
            sx={{
              position: "absolute",
              top: tableToolbarPosition.top,
              left: tableToolbarPosition.left,
              transform: "translateX(-50%)",
              zIndex: 100,
            }}
          >
            <TableToolbar editor={editor} onClose={hideTableToolbar} />
          </Box>
        )}
        <EditorContent editor={editor} />
      </Box>
    </Box>
  );
};

export default TipTapEditor;
